import React, { useState, useEffect, useContext } from 'react';
import { DashboardLayout, PageHeader } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert.js';
import Button from '../common/Button.js';
import companySettingsService from '../../services/companySettingsService.js';
import { formInputClass, formLabelClass, formSelectClass } from '../common/uiClasses.js';
import { Settings, Save, IndianRupee, Percent, Truck, ChevronDown, FileSpreadsheet, Scale, Shuffle, Layers, Wallet, Lock, Receipt, Calendar, Clock, ShieldCheck, CheckSquare, Square, RotateCcw, Users } from 'lucide-react';
import {
  CONFIGURABLE_ROLES,
  ROLE_MODULE_LABELS,
  ROLE_MODULE_ORDER,
  mergeRoleModuleAccess,
} from '../../utils/roleModuleAccess.js';
import { AuthContext } from '../../context/AuthContext.js';
import userService from '../../services/UserService.js';
import { REPORT_TYPE_OPTIONS, AGENT_MIS_TYPES } from '../report/reportConfig.js';

const DEFAULT_RCM = { enabled: false, igst: 5, cgst: 2.5, sgst: 2.5 };
const DEFAULT_DEMURRAGE = {
  enabled: false,
  free_hours: 24,
  clock_from: 'received',
  slabs: [
    { max_hours: 4, amount: 0 },
    { max_hours: 8, amount: 100 },
    { max_hours: '', amount: 200 },
  ],
};
const DEFAULT_CHARGE_ITEM = { enabled: false, amount: 0 };
const DEFAULT_SHIPMENT_CHARGES = {
  insurance: { ...DEFAULT_CHARGE_ITEM, mode: 'fixed' },
  docket: { ...DEFAULT_CHARGE_ITEM },
  handling: { ...DEFAULT_CHARGE_ITEM },
  door_pickup: { ...DEFAULT_CHARGE_ITEM },
  door_delivery: { ...DEFAULT_CHARGE_ITEM },
  loading: { ...DEFAULT_CHARGE_ITEM },
  unloading: { ...DEFAULT_CHARGE_ITEM },
  toll: { ...DEFAULT_CHARGE_ITEM },
  fuel_surcharge: { ...DEFAULT_CHARGE_ITEM },
  packing: { ...DEFAULT_CHARGE_ITEM },
};

const DEFAULT_EXPORT_COLUMNS = {
  quantity: true,
  freight: true,
  insurance: false,
  rate_per_kg: false,
  loading: false,
  unloading: false,
  toll: false,
  fuel_surcharge: false,
  packing: false,
  docket: true,
  other: true,
  paid: true,
  balance: true,
  mode: true,
  total_gst: true,
  gst_payer: true,
};

/** Optional columns after ROUTE — same order on PDF + Excel export */
const OPTIONAL_EXPORT_FIELDS = [
  { key: 'quantity', label: 'Quantity' },
  { key: 'freight', label: 'Freight' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'rate_per_kg', label: 'Rate / KG' },
  { key: 'loading', label: 'Loading' },
  { key: 'unloading', label: 'Unloading' },
  { key: 'toll', label: 'Toll' },
  { key: 'fuel_surcharge', label: 'Fuel Surcharge' },
  { key: 'packing', label: 'Packing' },
  { key: 'docket', label: 'Docket' },
  { key: 'other', label: 'Other' },
  { key: 'paid', label: 'Paid' },
  { key: 'balance', label: 'Balance' },
  { key: 'mode', label: 'Mode' },
];

const RCM_EXPORT_FIELDS = [
  { key: 'total_gst', label: 'Total GST' },
  { key: 'gst_payer', label: 'GST Payer' },
];

const CHARGE_FIELDS = [
  { key: 'insurance', label: 'Insurance' },
  { key: 'docket', label: 'Docket (₹)' },
  { key: 'handling', label: 'Handling (₹)' },
  { key: 'door_pickup', label: 'Door Pickup (₹)' },
  { key: 'door_delivery', label: 'Door Delivery (₹)' },
  { key: 'loading', label: 'Loading (₹)' },
  { key: 'unloading', label: 'Unloading (₹)' },
  { key: 'toll', label: 'Toll (₹)' },
  { key: 'fuel_surcharge', label: 'Fuel Surcharge (₹)' },
  { key: 'packing', label: 'Packing (₹)' },
];

/** Responsive settings card grid — 1 / 2 / 3 cols (mobile / iPad / desktop). */
const settingsGridClass = 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 min-w-0';

const ordinalDay = (day) => {
  const n = Number(day) || 1;
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

const CompanySettings = () => {
  const { user, patchUser } = useContext(AuthContext);
  const isInternalCompany = user?.network_mode !== 'EXTERNAL';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rcm, setRcm] = useState(DEFAULT_RCM);
  const [demurrage, setDemurrage] = useState(DEFAULT_DEMURRAGE);
  const [isCargoShipment, setIsCargoShipment] = useState(false);
  const [busFreightByWeight, setBusFreightByWeight] = useState(false);
  const [isCrossingEnabled, setIsCrossingEnabled] = useState(true);
  const [isAutoMlEnabled, setIsAutoMlEnabled] = useState(true);
  const [requirePaidSettlementBeforeMl, setRequirePaidSettlementBeforeMl] = useState(true);
  const [lockMlShipmentDetailsUntilRemit, setLockMlShipmentDetailsUntilRemit] = useState(true);
  const [shipmentReceiptSize, setShipmentReceiptSize] = useState('large');
  const [salaryCycleStartDay, setSalaryCycleStartDay] = useState(1);
  const [attendanceEnabled, setAttendanceEnabled] = useState(false);
  const [charges, setCharges] = useState(DEFAULT_SHIPMENT_CHARGES);
  const [exportColumns, setExportColumns] = useState(DEFAULT_EXPORT_COLUMNS);
  /** Accordion: only one settings section open at a time */
  const [openSection, setOpenSection] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [reportPermissions, setReportPermissions] = useState({});
  const [reportPermUserId, setReportPermUserId] = useState('');
  const [customizedReportUsers, setCustomizedReportUsers] = useState(() => new Set());
  const [roleModuleAccess, setRoleModuleAccess] = useState(() => mergeRoleModuleAccess(user?.company_features, {}, {
    crossingEnabled: user?.is_crossing_enabled !== false,
    networkMode: user?.network_mode,
  }));
  const [roleModuleRole, setRoleModuleRole] = useState('Accountant');

  const toggleSection = (key) => {
    setOpenSection((prev) => (prev === key ? null : key));
  };

  useEffect(() => {
    fetchSettings();
    fetchCompanyUsers();
  }, []);

  const defaultReportsForRole = (role) => {
    if (role === 'Agent') return [...AGENT_MIS_TYPES];
    return REPORT_TYPE_OPTIONS.map((r) => r.type);
  };

  const fetchCompanyUsers = async () => {
    try {
      const res = await userService.getAll(1, 500, { all: true });
      const list = res?.data || res?.users || [];
      const users = (Array.isArray(list) ? list : []).filter((u) => {
        const role = u.role_name || u.role;
        return role && role !== 'System Admin';
      });
      setCompanyUsers(users);
      if (users.length && !reportPermUserId) {
        setReportPermUserId(users[0].user_id);
      }
    } catch (err) {
      console.error('Failed to load users for report permissions', err);
    }
  };

  const mergeChargeDefaults = (raw) => {
    const merged = { ...DEFAULT_SHIPMENT_CHARGES };
    if (!raw || typeof raw !== 'object') return merged;
    Object.keys(merged).forEach((key) => {
      merged[key] = { ...DEFAULT_SHIPMENT_CHARGES[key], ...(raw[key] || {}) };
    });
    // Legacy fallback: old shipment_insurance_charge alone
    if (!raw.insurance && raw.enabled !== undefined) {
      merged.insurance = {
        enabled: !!raw.enabled,
        amount: raw.amount ?? 0,
        mode: raw.mode === 'percent' ? 'percent' : 'fixed',
      };
    }
    merged.insurance = {
      ...merged.insurance,
      mode: merged.insurance?.mode === 'percent' ? 'percent' : 'fixed',
    };
    return merged;
  };

  const mergeExportColumns = (raw, rcmEnabled) => {
    const merged = { ...DEFAULT_EXPORT_COLUMNS, ...(raw || {}) };
    Object.keys(DEFAULT_EXPORT_COLUMNS).forEach((key) => {
      merged[key] = !!merged[key];
    });
    if (rcmEnabled) {
      RCM_EXPORT_FIELDS.forEach(({ key }) => {
        merged[key] = true;
      });
    } else {
      RCM_EXPORT_FIELDS.forEach(({ key }) => {
        merged[key] = false;
      });
    }
    return merged;
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await companySettingsService.getSettings();
      const nextRcm = { ...DEFAULT_RCM, ...(res?.data?.rcm || {}) };
      setRcm(nextRcm);
      const nextDemurrage = { ...DEFAULT_DEMURRAGE, ...(res?.data?.demurrage || {}) };
      nextDemurrage.enabled = !!nextDemurrage.enabled;
      const freeH = parseFloat(nextDemurrage.free_hours);
      nextDemurrage.free_hours = Number.isFinite(freeH) ? freeH : DEFAULT_DEMURRAGE.free_hours;
      nextDemurrage.clock_from = nextDemurrage.clock_from === 'booked' ? 'booked' : 'received';
      nextDemurrage.slabs = Array.isArray(nextDemurrage.slabs) && nextDemurrage.slabs.length
        ? nextDemurrage.slabs.map((s) => ({
            max_hours: s.max_hours == null || s.max_hours === undefined ? '' : s.max_hours,
            amount: s.amount ?? 0,
          }))
        : DEFAULT_DEMURRAGE.slabs.map((s) => ({ ...s }));
      setDemurrage(nextDemurrage);
      const nextBusFreight = !!res?.data?.bus_freight_by_weight;
      const nextCargo = nextBusFreight ? false : !!res?.data?.is_cargo_shipment;
      setBusFreightByWeight(nextCargo ? false : nextBusFreight);
      setIsCargoShipment(nextCargo);
      setIsCrossingEnabled(res?.data?.is_crossing_enabled !== false);
      setIsAutoMlEnabled(res?.data?.is_auto_ml_enabled !== false);
      setRequirePaidSettlementBeforeMl(res?.data?.require_paid_settlement_before_ml !== false);
      setLockMlShipmentDetailsUntilRemit(res?.data?.lock_ml_shipment_details_until_remit !== false);
      setShipmentReceiptSize(res?.data?.shipment_receipt_size === 'short' ? 'short' : 'large');
      const cycleDay = parseInt(res?.data?.salary_cycle_start_day, 10);
      setSalaryCycleStartDay(Number.isFinite(cycleDay) ? Math.min(28, Math.max(1, cycleDay)) : 1);
      setAttendanceEnabled(!!res?.data?.attendance_enabled);
      setRoleModuleAccess(mergeRoleModuleAccess(
        res?.data?.company_features || user?.company_features,
        res?.data?.role_module_access,
        {
          crossingEnabled: res?.data?.is_crossing_enabled !== false,
          networkMode: user?.network_mode,
        },
      ));
      const chargeRaw =
        res?.data?.shipment_charge_defaults ||
        {
          ...(res?.data?.cargo_charge_defaults || {}),
          insurance: res?.data?.shipment_insurance_charge,
        };
      setCharges(mergeChargeDefaults(chargeRaw));
      setExportColumns(
        mergeExportColumns(res?.data?.shipment_export_columns, !!nextRcm.enabled),
      );
      const perms = res?.data?.report_permissions;
      setReportPermissions(perms && typeof perms === 'object' ? perms : {});
      setCustomizedReportUsers(new Set(Object.keys(perms && typeof perms === 'object' ? perms : {})));
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load company settings');
    } finally {
      setLoading(false);
    }
  };

  const handleRateChange = (key, value) => {
    setRcm((prev) => ({ ...prev, [key]: value }));
  };

  const handleChargeChange = (key, field, value) => {
    setCharges((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const handleExportColumnChange = (key, checked) => {
    if (rcm.enabled && RCM_EXPORT_FIELDS.some((f) => f.key === key)) {
      return;
    }
    setExportColumns((prev) => ({ ...prev, [key]: checked }));
  };

  const selectedReportUser = companyUsers.find((u) => String(u.user_id) === String(reportPermUserId));
  const selectedReportRole = selectedReportUser?.role_name || selectedReportUser?.role;
  const selectedReportLocked = ['Admin', 'Accountant'].includes(selectedReportRole);
  const selectedReportTypes = (() => {
    if (!reportPermUserId) return [];
    if (selectedReportLocked) return REPORT_TYPE_OPTIONS.map((r) => r.type);
    if (customizedReportUsers.has(String(reportPermUserId)) && Array.isArray(reportPermissions[reportPermUserId])) {
      return reportPermissions[reportPermUserId];
    }
    return defaultReportsForRole(selectedReportRole);
  })();

  const markReportUserCustomized = (userId, types) => {
    const uid = String(userId);
    setReportPermissions((prev) => ({ ...prev, [uid]: types }));
    setCustomizedReportUsers((prev) => {
      const next = new Set(prev);
      next.add(uid);
      return next;
    });
  };

  const toggleReportTypeForUser = (reportType, checked) => {
    if (!reportPermUserId || selectedReportLocked) return;
    const current = new Set(selectedReportTypes);
    if (checked) current.add(reportType);
    else current.delete(reportType);
    markReportUserCustomized(reportPermUserId, [...current]);
  };

  const toggleAllReportsForUser = (checked) => {
    if (!reportPermUserId || selectedReportLocked) return;
    markReportUserCustomized(
      reportPermUserId,
      checked ? REPORT_TYPE_OPTIONS.map((r) => r.type) : [],
    );
  };

  const resetReportUserToDefaults = () => {
    if (!reportPermUserId || selectedReportLocked) return;
    const uid = String(reportPermUserId);
    setReportPermissions((prev) => {
      const next = { ...prev };
      delete next[uid];
      return next;
    });
    setCustomizedReportUsers((prev) => {
      const next = new Set(prev);
      next.delete(uid);
      return next;
    });
  };

  const handleRcmToggle = (checked) => {
    setRcm((prev) => ({ ...prev, enabled: checked }));
    setExportColumns((prev) => mergeExportColumns(prev, checked));
  };

  /** Bus freight is for bus bookings only — mutually exclusive with cargo mode */
  const handleBusFreightToggle = (checked) => {
    setBusFreightByWeight(checked);
    if (checked) {
      setIsCargoShipment(false);
    }
  };

  const handleCargoToggle = (checked) => {
    setIsCargoShipment(checked);
    if (checked) {
      setBusFreightByWeight(false);
    }
  };

  const handleSave = async () => {
    const chargePayload = {};
    Object.keys(DEFAULT_SHIPMENT_CHARGES).forEach((key) => {
      chargePayload[key] = {
        enabled: !!charges[key]?.enabled,
        amount: parseFloat(charges[key]?.amount) || 0,
      };
      if (key === 'insurance') {
        chargePayload[key].mode = charges[key]?.mode === 'percent' ? 'percent' : 'fixed';
      }
    });

    const exportPayload = { ...DEFAULT_EXPORT_COLUMNS };
    Object.keys(DEFAULT_EXPORT_COLUMNS).forEach((key) => {
      exportPayload[key] = !!exportColumns[key];
    });
    if (rcm.enabled) {
      RCM_EXPORT_FIELDS.forEach(({ key }) => {
        exportPayload[key] = true;
      });
    }

    const payload = {
      rcm: {
        enabled: !!rcm.enabled,
        igst: parseFloat(rcm.igst) || 0,
        cgst: parseFloat(rcm.cgst) || 0,
        sgst: parseFloat(rcm.sgst) || 0,
      },
      demurrage: {
        enabled: !!demurrage.enabled,
        free_hours: parseFloat(demurrage.free_hours) || 0,
        clock_from: demurrage.clock_from === 'booked' ? 'booked' : 'received',
        slabs: (demurrage.slabs || []).map((s) => ({
          max_hours: s.max_hours === '' || s.max_hours == null ? null : parseFloat(s.max_hours),
          amount: parseFloat(s.amount) || 0,
        })),
      },
      // Bus freight by weight cannot run with cargo mode
      is_cargo_shipment: !!isCargoShipment,
      bus_freight_by_weight: isCargoShipment ? false : !!busFreightByWeight,
      shipment_charge_defaults: chargePayload,
      shipment_export_columns: exportPayload,
      shipment_receipt_size: shipmentReceiptSize === 'short' ? 'short' : 'large',
      require_paid_settlement_before_ml: !!requirePaidSettlementBeforeMl,
      lock_ml_shipment_details_until_remit: !!lockMlShipmentDetailsUntilRemit,
    };
    if (attendanceEnabled) {
      payload.salary_cycle_start_day = salaryCycleStartDay;
    }
    if (isInternalCompany) {
      payload.is_crossing_enabled = !!isCrossingEnabled;
      payload.is_auto_ml_enabled = !!isAutoMlEnabled;
    }

    // Only persist users that were customized in company settings
    const permPayload = {};
    customizedReportUsers.forEach((uid) => {
      permPayload[uid] = Array.isArray(reportPermissions[uid])
        ? reportPermissions[uid]
        : [];
    });
    payload.report_permissions = permPayload;
    payload.role_module_access = roleModuleAccess;

    if (payload.rcm.igst < 0 || payload.rcm.cgst < 0 || payload.rcm.sgst < 0) {
      Alert.error('GST rates cannot be negative');
      return;
    }
    if (Object.values(chargePayload).some((c) => c.amount < 0)) {
      Alert.error('Charge amounts cannot be negative');
      return;
    }

    try {
      setSaving(true);
      const res = await companySettingsService.updateSettings(payload);
      const nextRcm = { ...DEFAULT_RCM, ...(res?.data?.rcm || payload.rcm) };
      setRcm(nextRcm);
      const savedDem = { ...DEFAULT_DEMURRAGE, ...(res?.data?.demurrage || payload.demurrage) };
      savedDem.enabled = !!savedDem.enabled;
      savedDem.clock_from = savedDem.clock_from === 'booked' ? 'booked' : 'received';
      savedDem.slabs = Array.isArray(savedDem.slabs) && savedDem.slabs.length
        ? savedDem.slabs.map((s) => ({
            max_hours: s.max_hours == null ? '' : s.max_hours,
            amount: s.amount ?? 0,
          }))
        : DEFAULT_DEMURRAGE.slabs.map((s) => ({ ...s }));
      setDemurrage(savedDem);
      const savedCargo = !!(res?.data?.is_cargo_shipment ?? payload.is_cargo_shipment);
      const savedBusFreight = savedCargo
        ? false
        : !!(res?.data?.bus_freight_by_weight ?? payload.bus_freight_by_weight);
      setBusFreightByWeight(savedBusFreight);
      setIsCargoShipment(savedCargo);
      setIsCrossingEnabled(
        (res?.data?.is_crossing_enabled ?? payload.is_crossing_enabled) !== false,
      );
      setIsAutoMlEnabled(
        (res?.data?.is_auto_ml_enabled ?? payload.is_auto_ml_enabled) !== false,
      );
      setRequirePaidSettlementBeforeMl(
        (res?.data?.require_paid_settlement_before_ml ?? payload.require_paid_settlement_before_ml) !== false,
      );
      setLockMlShipmentDetailsUntilRemit(
        (res?.data?.lock_ml_shipment_details_until_remit ?? payload.lock_ml_shipment_details_until_remit) !== false,
      );
      const savedReceiptSize =
        (res?.data?.shipment_receipt_size ?? payload.shipment_receipt_size) === 'short' ? 'short' : 'large';
      setShipmentReceiptSize(savedReceiptSize);
      const savedCycle = parseInt(
        res?.data?.salary_cycle_start_day ?? payload.salary_cycle_start_day,
        10,
      );
      if (Number.isFinite(savedCycle)) {
        setSalaryCycleStartDay(Math.min(28, Math.max(1, savedCycle)));
      }
      setAttendanceEnabled(
        res?.data?.attendance_enabled !== undefined
          ? !!res.data.attendance_enabled
          : attendanceEnabled,
      );
      setRoleModuleAccess(mergeRoleModuleAccess(
        res?.data?.company_features || user?.company_features,
        res?.data?.role_module_access,
        {
          crossingEnabled: res?.data?.is_crossing_enabled !== false,
          networkMode: user?.network_mode,
        },
      ));
      if (typeof patchUser === 'function') {
        patchUser({ role_module_access: res?.data?.role_module_access || {} });
      }
      const savedPerms = res?.data?.report_permissions;
      if (savedPerms && typeof savedPerms === 'object') {
        setReportPermissions(savedPerms);
        setCustomizedReportUsers(new Set(Object.keys(savedPerms)));
      }
      if (typeof patchUser === 'function' && isInternalCompany) {
        patchUser({
          is_cargo_shipment: savedCargo,
          is_crossing_enabled:
            (res?.data?.is_crossing_enabled ?? payload.is_crossing_enabled) !== false,
          is_auto_ml_enabled:
            (res?.data?.is_auto_ml_enabled ?? payload.is_auto_ml_enabled) !== false,
          require_paid_settlement_before_ml:
            (res?.data?.require_paid_settlement_before_ml ?? payload.require_paid_settlement_before_ml) !== false,
          lock_ml_shipment_details_until_remit:
            (res?.data?.lock_ml_shipment_details_until_remit ?? payload.lock_ml_shipment_details_until_remit) !== false,
          shipment_receipt_size: savedReceiptSize,
        });
      } else if (typeof patchUser === 'function') {
        patchUser({
          is_cargo_shipment: savedCargo,
          shipment_receipt_size: savedReceiptSize,
          require_paid_settlement_before_ml:
            (res?.data?.require_paid_settlement_before_ml ?? payload.require_paid_settlement_before_ml) !== false,
          lock_ml_shipment_details_until_remit:
            (res?.data?.lock_ml_shipment_details_until_remit ?? payload.lock_ml_shipment_details_until_remit) !== false,
        });
      }
      setCharges(
        mergeChargeDefaults(res?.data?.shipment_charge_defaults || chargePayload),
      );
      setExportColumns(
        mergeExportColumns(
          res?.data?.shipment_export_columns || exportPayload,
          !!nextRcm.enabled,
        ),
      );
      Alert.success('Settings saved successfully');
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12 sm:py-20">
          <Loader fullScreen={false} size="default" message="Loading settings..." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto min-w-0">
        <PageHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Settings className="w-5 h-5 sm:w-6 sm:h-6 text-secondary shrink-0" />
              Company Settings
            </span>
          }
          subtitle="Manage settings for your company"
        >
          <Button
            variant="primary"
            onClick={handleSave}
            loading={saving}
            icon={Save}
            mobileIconOnly
            mobileLabel="Save"
          >
            Save Settings
          </Button>
        </PageHeader>

        <div className={settingsGridClass}>
          <SettingCard
            icon={Percent}
            iconTone="bg-sky-100 text-sky-600"
            title="RCM (Reverse Charge Mechanism)"
            description="When enabled, GST on new shipments is auto-derived from GST registration and who pays. Fallback rates apply when nobody is registered."
            enabled={!!rcm.enabled}
            onToggle={handleRcmToggle}
            isOpen={openSection === 'rcm'}
            onToggleOpen={() => toggleSection('rcm')}
          >
            <div
              className={`grid grid-cols-3 gap-2 ${rcm.enabled ? '' : 'opacity-50 pointer-events-none'}`}
            >
              <RateInput
                label="IGST %"
                value={rcm.igst}
                onChange={(v) => handleRateChange('igst', v)}
              />
              <RateInput
                label="CGST %"
                value={rcm.cgst}
                onChange={(v) => handleRateChange('cgst', v)}
              />
              <RateInput
                label="SGST %"
                value={rcm.sgst}
                onChange={(v) => handleRateChange('sgst', v)}
              />
            </div>
            <p className="text-[11px] text-gray-400 mt-2 leading-relaxed">
              Same-state: CGST + SGST. Different-state: IGST.
            </p>
          </SettingCard>

          {isInternalCompany && (
            <SettingCard
              icon={Clock}
              iconTone="bg-rose-100 text-rose-700"
              title="Demurrage (late delivery)"
              description="After the free hours from Received (or Booked), apply a fixed ₹ slab. Delivered is blocked until the extra amount is collected as Cash or UPI."
              enabled={!!demurrage.enabled}
              onToggle={(checked) => setDemurrage((prev) => ({ ...prev, enabled: checked }))}
              isOpen={openSection === 'demurrage'}
              onToggleOpen={() => toggleSection('demurrage')}
            >
              <div className={`space-y-3 ${demurrage.enabled ? '' : 'opacity-50 pointer-events-none'}`}>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={formLabelClass}>Free hours</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={demurrage.free_hours}
                      onChange={(e) => setDemurrage((prev) => ({ ...prev, free_hours: e.target.value }))}
                      className={formInputClass}
                    />
                  </div>
                  <div>
                    <label className={formLabelClass}>Clock starts from</label>
                    <select
                      value={demurrage.clock_from}
                      onChange={(e) => setDemurrage((prev) => ({ ...prev, clock_from: e.target.value }))}
                      className={formSelectClass}
                    >
                      <option value="received">Received</option>
                      <option value="booked">Booked</option>
                    </select>
                  </div>
                </div>
                <p className="text-[11px] text-gray-400">Slabs are fixed rupees. Leave max hours blank for the last (open-ended) slab.</p>
                {(demurrage.slabs || []).map((slab, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      placeholder="Max hrs"
                      value={slab.max_hours}
                      onChange={(e) => {
                        const slabs = [...demurrage.slabs];
                        slabs[idx] = { ...slabs[idx], max_hours: e.target.value };
                        setDemurrage((prev) => ({ ...prev, slabs }));
                      }}
                      className={`${formInputClass} w-24`}
                    />
                    <span className="text-xs text-gray-500">hrs → ₹</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={slab.amount}
                      onChange={(e) => {
                        const slabs = [...demurrage.slabs];
                        slabs[idx] = { ...slabs[idx], amount: e.target.value };
                        setDemurrage((prev) => ({ ...prev, slabs }));
                      }}
                      className={`${formInputClass} w-28`}
                    />
                    <button
                      type="button"
                      className="text-xs text-red-500"
                      onClick={() => setDemurrage((prev) => ({
                        ...prev,
                        slabs: prev.slabs.filter((_, i) => i !== idx),
                      }))}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="text-xs font-medium text-blue-600"
                  onClick={() => setDemurrage((prev) => ({
                    ...prev,
                    slabs: [...(prev.slabs || []), { max_hours: '', amount: 0 }],
                  }))}
                >
                  Add slab
                </button>
              </div>
            </SettingCard>
          )}

          <SettingCard
            icon={Truck}
            iconTone="bg-orange-100 text-orange-600"
            title="Cargo Shipment Mode"
            description="Off = Bus booking (default). On = Cargo/pallet booking with package dimensions, CBM, and volumetric weight."
            enabled={!!isCargoShipment}
            onToggle={handleCargoToggle}
            isOpen={openSection === 'cargo'}
            onToggleOpen={() => toggleSection('cargo')}
          >
            <p className="text-[11px] text-gray-400 leading-relaxed">
              Applies to new bookings only. Existing shipments keep their original type. Enabling Cargo hides Bus Freight by Weight.
            </p>
          </SettingCard>

          {!isCargoShipment && (
            <SettingCard
              icon={Scale}
              iconTone="bg-sky-100 text-sky-600"
              title="Bus Freight by Weight"
              description="Off (default) = freight from qty × article rate. On = freight from Rate/KG × weight (article rate field becomes weight)."
              enabled={!!busFreightByWeight}
              onToggle={handleBusFreightToggle}
              isOpen={openSection === 'busFreight'}
              onToggleOpen={() => toggleSection('busFreight')}
            >
              <p className="text-[11px] text-gray-400 leading-relaxed">
                Only for normal (bus) bookings. Turning this on disables Cargo Shipment Mode. Enable Rate / KG under Shipment Charges to set the default rate.
              </p>
            </SettingCard>
          )}

          {isInternalCompany && (
            <SettingCard
              icon={Shuffle}
              iconTone="bg-indigo-100 text-indigo-600"
              title="Shipment Crossing"
              description="When enabled, Crossing appears in the sidebar and in Scan QR. Internal companies only."
              enabled={!!isCrossingEnabled}
              onToggle={setIsCrossingEnabled}
              isOpen={openSection === 'crossing'}
              onToggleOpen={() => toggleSection('crossing')}
            >
              <p className="text-[11px] text-gray-400 leading-relaxed">
                Off hides Crossing from the sidebar and the Scan QR Crossing tab, and blocks crossing APIs.
              </p>
            </SettingCard>
          )}

          {isInternalCompany && (
            <SettingCard
              icon={Layers}
              iconTone="bg-emerald-100 text-emerald-600"
              title="Auto ML Creation"
              description="When enabled, Add Shipment shows the Auto ML Creation block to create or attach an ML sheet automatically."
              enabled={!!isAutoMlEnabled}
              onToggle={setIsAutoMlEnabled}
              isOpen={openSection === 'autoMl'}
              onToggleOpen={() => toggleSection('autoMl')}
            >
              <p className="text-[11px] text-gray-400 leading-relaxed">
                Off hides Auto ML Creation on Add Shipment. Internal companies only.
              </p>
            </SettingCard>
          )}

          <SettingCard
              icon={Wallet}
              iconTone="bg-amber-100 text-amber-700"
              title="Require Paid Settlement Before ML"
              description="When ON, agents must settle Paid collections (Paid Amount Settlement) before they can create an ML sheet."
              enabled={!!requirePaidSettlementBeforeMl}
              onToggle={setRequirePaidSettlementBeforeMl}
              isOpen={openSection === 'paidSettleBeforeMl'}
              onToggleOpen={() => toggleSection('paidSettleBeforeMl')}
            >
              <p className="text-[11px] text-gray-400 leading-relaxed">
                OFF = agents can create ML even with unremitted Paid cash. Paid Amount Settlement page stays available.
              </p>
            </SettingCard>

          <SettingCard
              icon={Lock}
              iconTone="bg-rose-100 text-rose-600"
              title="Lock ML Shipment Details Until Remit"
              description="When ON, agents cannot see Included Shipments on an ML sheet until they remit that sheet’s To-Pay via Return to Company."
              enabled={!!lockMlShipmentDetailsUntilRemit}
              onToggle={setLockMlShipmentDetailsUntilRemit}
              isOpen={openSection === 'lockMlDetails'}
              onToggleOpen={() => toggleSection('lockMlDetails')}
            >
              <p className="text-[11px] text-gray-400 leading-relaxed">
                OFF = agents always see shipment details on View ML, even if To-Pay is still outstanding.
              </p>
            </SettingCard>

          <SettingCard
            icon={Receipt}
            iconTone="bg-teal-100 text-teal-600"
            title="Shipment Receipt"
            description="Prints one Goods Consignment Booking Receipt body per action (header omitted for pre-printed paper), sized to the top half of A4."
            enabled={shipmentReceiptSize === 'short'}
            onToggle={(checked) => setShipmentReceiptSize(checked ? 'short' : 'large')}
            isOpen={openSection === 'receiptSize'}
            onToggleOpen={() => toggleSection('receiptSize')}
          >
            <div className="space-y-2 min-w-0">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="shipment_receipt_size"
                  checked={shipmentReceiptSize === 'large'}
                  onChange={() => setShipmentReceiptSize('large')}
                  className="w-4 h-4 mt-0.5 text-buttonBg focus:ring-blue-400"
                />
                <span className="min-w-0">
                  <span className="block text-xs sm:text-sm font-medium text-secondary">Pre-printed paper (default)</span>
                  <span className="block text-[11px] text-gray-400 leading-relaxed">Single copy, top half of A4, under the physical company header. Bottom half stays blank.</span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="shipment_receipt_size"
                  checked={shipmentReceiptSize === 'short'}
                  onChange={() => setShipmentReceiptSize('short')}
                  className="w-4 h-4 mt-0.5 text-buttonBg focus:ring-blue-400"
                />
                <span className="min-w-0">
                  <span className="block text-xs sm:text-sm font-medium text-secondary">Short</span>
                  <span className="block text-[11px] text-gray-400 leading-relaxed">Same single pre-printed copy. Office + customer side-by-side strips are no longer printed.</span>
                </span>
              </label>
            </div>
          </SettingCard>

          {attendanceEnabled && (
            <SettingCard
              icon={Calendar}
              iconTone="bg-blue-100 text-blue-600"
              title="Salary Cycle"
              description="Attendance for a selected salary month is counted from this day of the previous month up to the day before this day of the selected month. Day 1 uses the calendar month."
              hideToggle
              isOpen={openSection === 'salaryCycle'}
              onToggleOpen={() => toggleSection('salaryCycle')}
            >
              <div className="space-y-2 min-w-0">
                <label className={formLabelClass} htmlFor="salary_cycle_start_day">
                  Cycle start day
                </label>
                <select
                  id="salary_cycle_start_day"
                  value={salaryCycleStartDay}
                  onChange={(e) => setSalaryCycleStartDay(parseInt(e.target.value, 10) || 1)}
                  className={formSelectClass()}
                >
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                    <option key={day} value={day}>
                      {day === 1
                        ? '1 — Calendar month (1st to last day)'
                        : `${day} — ${ordinalDay(day)} to ${ordinalDay(day)}`}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-400 leading-relaxed">
                  {salaryCycleStartDay <= 1
                    ? 'July salary uses 1 Jul through 31 Jul.'
                    : `Example: July salary uses the ${ordinalDay(salaryCycleStartDay)} of June through the ${ordinalDay(salaryCycleStartDay - 1)} of July.`}
                </p>
              </div>
            </SettingCard>
          )}

          <SettingCard
            icon={IndianRupee}
            iconTone="bg-emerald-100 text-emerald-600"
            title="Shipment Charges"
            description="Shared default charges for bus and cargo. Enable each charge to show it on the shipment page with this default amount. Rate / KG is set per Location Hub (receiver)."
            enabled={Object.values(charges).some((c) => c.enabled)}
            onToggle={(checked) => {
              setCharges((prev) => {
                const next = { ...prev };
                Object.keys(next).forEach((key) => {
                  next[key] = { ...next[key], enabled: checked };
                });
                return next;
              });
            }}
            isOpen={openSection === 'charges'}
            onToggleOpen={() => toggleSection('charges')}
          >
            <div className="space-y-2 min-w-0">
              {CHARGE_FIELDS.map(({ key, label }) => {
                const isInsurance = key === 'insurance';
                const insuranceMode = charges.insurance?.mode === 'percent' ? 'percent' : 'fixed';
                const displayLabel = isInsurance
                  ? (insuranceMode === 'percent' ? 'Insurance (%)' : 'Insurance (₹)')
                  : label;
                return (
                <div
                  key={key}
                  className={`flex items-center gap-1.5 sm:gap-2 min-w-0 w-full ${
                    charges[key]?.enabled ? '' : 'opacity-50'
                  }`}
                >
                  <Toggle
                    checked={!!charges[key]?.enabled}
                    onChange={(checked) => handleChargeChange(key, 'enabled', checked)}
                  />
                  <span className="text-xs sm:text-sm font-medium text-secondary flex-1 min-w-0 truncate">
                    {displayLabel}
                  </span>
                  {isInsurance && (
                    <div className="flex shrink-0 rounded-lg border border-gray-300 overflow-hidden text-[10px] sm:text-xs font-medium">
                      <button
                        type="button"
                        disabled={!charges.insurance?.enabled}
                        onClick={() => handleChargeChange('insurance', 'mode', 'fixed')}
                        className={`px-1.5 sm:px-2 py-1 ${
                          insuranceMode === 'fixed'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        } disabled:cursor-not-allowed`}
                      >
                        Fixed
                      </button>
                      <button
                        type="button"
                        disabled={!charges.insurance?.enabled}
                        onClick={() => handleChargeChange('insurance', 'mode', 'percent')}
                        className={`px-1.5 sm:px-2 py-1 border-l border-gray-300 ${
                          insuranceMode === 'percent'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        } disabled:cursor-not-allowed`}
                      >
                        %
                      </button>
                    </div>
                  )}
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={charges[key]?.amount ?? 0}
                    onChange={(e) => handleChargeChange(key, 'amount', e.target.value)}
                    disabled={!charges[key]?.enabled}
                    className="w-14 sm:w-20 md:w-24 shrink-0 px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                  />
                </div>
                );
              })}
            </div>
          </SettingCard>

          <SettingCard
            icon={FileSpreadsheet}
            iconTone="bg-violet-100 text-violet-600"
            title="Shipment Export Columns"
            description="Choose columns after Route for the shipment list PDF and the shipment report Excel. SR through Route are always included."
            enabled={OPTIONAL_EXPORT_FIELDS.some((f) => exportColumns[f.key])}
            onToggle={(checked) => {
              setExportColumns((prev) => {
                const next = { ...prev };
                OPTIONAL_EXPORT_FIELDS.forEach(({ key }) => {
                  next[key] = checked;
                });
                if (rcm.enabled) {
                  RCM_EXPORT_FIELDS.forEach(({ key }) => {
                    next[key] = true;
                  });
                }
                return next;
              });
            }}
            isOpen={openSection === 'exportColumns'}
            onToggleOpen={() => toggleSection('exportColumns')}
          >
            <div className="space-y-2 min-w-0 max-h-72 overflow-y-auto pr-1">
              <p className="text-[11px] text-gray-400 leading-relaxed mb-1">
                Always: SR, Shipment ID, Date, Sender, Receiver, Route
              </p>
              {OPTIONAL_EXPORT_FIELDS.map(({ key, label }) => (
                <label
                  key={key}
                  className="flex items-center gap-2 min-w-0 w-full cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!!exportColumns[key]}
                    onChange={(e) => handleExportColumnChange(key, e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-buttonBg focus:ring-blue-400"
                  />
                  <span className="text-xs sm:text-sm font-medium text-secondary">
                    {label}
                  </span>
                </label>
              ))}
              {rcm.enabled && (
                <>
                  <p className="text-[11px] text-gray-400 leading-relaxed pt-1">
                    Required when RCM is enabled
                  </p>
                  {RCM_EXPORT_FIELDS.map(({ key, label }) => (
                    <label
                      key={key}
                      className="flex items-center gap-2 min-w-0 w-full opacity-80 cursor-not-allowed"
                    >
                      <input
                        type="checkbox"
                        checked
                        disabled
                        className="w-4 h-4 rounded border-gray-300 text-buttonBg"
                      />
                      <span className="text-xs sm:text-sm font-medium text-secondary">
                        {label}
                      </span>
                    </label>
                  ))}
                </>
              )}
            </div>
          </SettingCard>

          <div className="md:col-span-2 lg:col-span-3">
            <SettingCard
              icon={Users}
              iconTone="bg-indigo-100 text-indigo-700"
              title="Role Module Access"
              description="Choose a role. Every sidebar item that role already has is selected. Uncheck one to hide it. Admin always keeps the full sidebar. Attendance also removes the header clock and the Attendance option while scanning a QR."
              hideToggle
              isOpen={openSection === 'roleModules'}
              onToggleOpen={() => toggleSection('roleModules')}
            >
              <RoleModuleAccessFields
                role={roleModuleRole}
                onRoleChange={setRoleModuleRole}
                access={roleModuleAccess}
                onToggle={(moduleKey, checked) => {
                  setRoleModuleAccess((prev) => ({
                    ...prev,
                    [roleModuleRole]: {
                      ...(prev[roleModuleRole] || {}),
                      [moduleKey]: checked,
                    },
                  }));
                }}
              />
            </SettingCard>
          </div>

          <SettingCard
            icon={ShieldCheck}
            iconTone="bg-sky-100 text-sky-700"
            title="Report Permissions"
            description="Choose which MIS reports each user can open. Admin and Accountant always have all reports. Users without a custom list keep their role defaults."
            enabled={customizedReportUsers.size > 0}
            hideToggle
            isOpen={openSection === 'reportPermissions'}
            onToggleOpen={() => toggleSection('reportPermissions')}
          >
            <div className="space-y-3 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-3">
                <div className="flex-1 min-w-0">
                  <label className={formLabelClass}>User</label>
                  <select
                    value={reportPermUserId}
                    onChange={(e) => setReportPermUserId(e.target.value)}
                    className={formSelectClass()}
                  >
                    {companyUsers.length === 0 && (
                      <option value="">No users found</option>
                    )}
                    {companyUsers.map((u) => (
                      <option key={u.user_id} value={u.user_id}>
                        {(u.name || u.user_id)} ({u.role_name || u.role || '—'})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 pb-0.5">
                  {!selectedReportLocked && (
                    <>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        icon={CheckSquare}
                        title="Select all"
                        aria-label="Select all"
                        className="!px-2 !py-1.5"
                        onClick={() => toggleAllReportsForUser(true)}
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        icon={Square}
                        title="Clear"
                        aria-label="Clear"
                        className="!px-2 !py-1.5"
                        onClick={() => toggleAllReportsForUser(false)}
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        icon={RotateCcw}
                        title="Role defaults"
                        aria-label="Role defaults"
                        className="!px-2 !py-1.5"
                        onClick={resetReportUserToDefaults}
                      />
                    </>
                  )}
                  <span className={`text-[10px] font-semibold px-2 py-1 rounded-full border whitespace-nowrap ${
                    selectedReportLocked
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : customizedReportUsers.has(String(reportPermUserId))
                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                        : 'bg-gray-50 text-gray-600 border-gray-200'
                  }`}
                  >
                    {selectedReportLocked
                      ? 'Full access'
                      : customizedReportUsers.has(String(reportPermUserId))
                        ? 'Custom'
                        : 'Defaults'}
                  </span>
                </div>
              </div>
              <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                <div className="hidden sm:grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500 font-semibold border-b border-gray-200">
                  <div className="col-span-1">Allow</div>
                  <div className="col-span-4">Report name</div>
                  <div className="col-span-3">Category</div>
                  <div className="col-span-4">Description</div>
                </div>
                <div className="divide-y divide-gray-100 max-h-[17.5rem] overflow-y-auto overscroll-contain">
                  {REPORT_TYPE_OPTIONS.map(({ type, title, description, categoryTitle }) => {
                    const checked = selectedReportTypes.includes(type);
                    return (
                      <label
                        key={type}
                        className={`grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-2 px-3 py-2.5 items-start sm:items-center min-w-0 ${
                          selectedReportLocked ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'
                        }`}
                      >
                        <div className="sm:col-span-1 flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={selectedReportLocked || !reportPermUserId}
                            onChange={(e) => toggleReportTypeForUser(type, e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 text-buttonBg focus:ring-blue-400"
                          />
                          <span className="sm:hidden text-xs font-semibold text-secondary">{title}</span>
                        </div>
                        <div className="sm:col-span-4 min-w-0 hidden sm:block">
                          <p className="text-xs sm:text-sm font-semibold text-secondary truncate">{title}</p>
                          <p className="text-[10px] text-gray-400 font-mono truncate">{type}</p>
                        </div>
                        <div className="sm:col-span-3 min-w-0">
                          <span className="inline-flex text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100">
                            {categoryTitle}
                          </span>
                          <p className="sm:hidden text-[11px] text-gray-500 mt-1 leading-snug">{description}</p>
                        </div>
                        <div className="sm:col-span-4 min-w-0 hidden sm:block">
                          <p className="text-[11px] sm:text-xs text-gray-500 leading-snug line-clamp-2">{description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50">
                  <span className="text-[11px] text-gray-500">
                    {REPORT_TYPE_OPTIONS.length} reports · scroll for more
                  </span>
                </div>
              </div>
            </div>
          </SettingCard>
        </div>

        <div className="mt-4 sm:mt-6 flex justify-end sm:hidden">
          <Button variant="primary" onClick={handleSave} loading={saving} icon={Save} fullWidth>
            Save Settings
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

function RoleModuleAccessFields({ role, onRoleChange, access, onToggle }) {
  const modules = access?.[role] || {};
  const keys = ROLE_MODULE_ORDER.filter((key) => Object.prototype.hasOwnProperty.call(modules, key));

  return (
    <div className="space-y-3 min-w-0">
      <div>
        <label className={formLabelClass} htmlFor="role_module_role">Role</label>
        <select
          id="role_module_role"
          value={role}
          onChange={(e) => onRoleChange(e.target.value)}
          className={formSelectClass()}
        >
          {CONFIGURABLE_ROLES.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
      </div>
      {keys.length === 0 ? (
        <p className="text-[11px] text-gray-400 leading-relaxed">
          No modules are turned on for this company, so there is nothing to assign for this role.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {keys.map((key) => (
            <label key={key} className="flex items-center gap-2 min-w-0 cursor-pointer">
              <input
                type="checkbox"
                checked={modules[key] !== false}
                onChange={(e) => onToggle(key, e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-buttonBg focus:ring-blue-400"
              />
              <span className="text-xs sm:text-sm font-medium text-secondary truncate">
                {ROLE_MODULE_LABELS[key] || key}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingCard({
  icon: Icon,
  iconTone,
  title,
  description,
  enabled,
  onToggle,
  toggleDisabled = false,
  hideToggle = false,
  isOpen,
  onToggleOpen,
  children,
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4 md:p-5 flex flex-col min-h-0 min-w-0 h-full overflow-hidden">
      <div className="flex items-start justify-between gap-2 min-w-0">
        <button
          type="button"
          onClick={onToggleOpen}
          className="flex items-start gap-2 min-w-0 flex-1 text-left"
          aria-expanded={isOpen}
        >
          <div
            className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${iconTone}`}
          >
            <Icon className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm sm:text-base font-semibold text-secondary leading-snug">
              {title}
            </h2>
          </div>
          <ChevronDown
            className={`w-5 h-5 text-gray-400 shrink-0 mt-0.5 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
        {!hideToggle && (
          <div
            onClick={(e) => e.stopPropagation()}
            className={toggleDisabled ? 'opacity-50' : ''}
            title={toggleDisabled ? 'Disabled while Bus Freight by Weight is on' : undefined}
          >
            <Toggle checked={enabled} onChange={onToggle} disabled={toggleDisabled} />
          </div>
        )}
      </div>

      {isOpen && (
        <div className="mt-2 sm:mt-3 min-w-0">
          <p className="text-xs text-gray-500 leading-relaxed mb-3">{description}</p>
          <div className="min-w-0">{children}</div>
        </div>
      )}
    </div>
  );
}

function Toggle({ checked, onChange, disabled = false }) {
  return (
    <label
      className={`relative inline-flex items-center shrink-0 mt-0.5 ${
        disabled ? 'cursor-not-allowed' : 'cursor-pointer'
      }`}
    >
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        disabled={disabled}
        onChange={(e) => {
          if (disabled) return;
          onChange(e.target.checked);
        }}
      />
      <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-buttonBg peer-disabled:opacity-60" />
    </label>
  );
}

function RateInput({ label, value, onChange }) {
  return (
    <div className="min-w-0">
      <label className={formLabelClass}>{label}</label>
      <input
        type="number"
        min="0"
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={formInputClass()}
      />
    </div>
  );
}

export default CompanySettings;
